import { VStack } from '@web/components/atoms/Layout'
import { ProfileData } from '@web/graphql-types'
import LeaderboardAvatar from './LeaderboardAvatar'
import { Body2, Section } from '@web/components/atoms/Typography'
import { ChakraProgress } from '@web/components/atoms/Progress'
import { toDisplayAmountNoDecimal } from '@web/components/utils/format'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { DEFAULT_LEADERBOARD } from '../default'

type LeaderboardProfileProps = {
  data: ProfileData
  index: number
  styles: Record<string, string>
}

const LeaderboardProfile = ({ data, index, styles }: LeaderboardProfileProps) => {
  const { name } = data
  const avatarUrl = data.logoUrl ?? ''
  const goal = data.goal ?? 0
  const amountRaised = data.progressAmount ?? 0

  const progress = Math.min(Math.round((amountRaised / (goal || 1)) * 100), 100)
  const progressColor = useColorWithOpacity(
    styles?.progressBarColor,
    '1',
    DEFAULT_LEADERBOARD.styles?.profile?.progressBarColor,
  )
  const progressBackgroundColor = useColorWithOpacity(
    styles?.progressBarBackgroundColor,
    '1',
    DEFAULT_LEADERBOARD.styles?.profile?.progressBarBackgroundColor,
  )

  const backgroundColor = useColorWithOpacity(styles?.backgroundColor, styles?.backgroundColorOpacity)
  const borderColor = useColorWithOpacity(styles?.borderColor, styles?.borderColorOpacity)
  const textColor = useColorWithOpacity(styles?.textColor, styles?.textColorOpacity)

  return (
    <VStack
      p="24px"
      bgColor={backgroundColor}
      border="1px solid transparent"
      borderColor={borderColor}
      color={textColor}
      borderRadius="8px"
      className="c-leaderboard-profile"
      flexDir="column"
      alignSelf="stretch"
      justifyContent="space-between"
      asChild={true}
      maxW="263px"
    >
      <a {...(data.profileUrl ? { href: data.profileUrl } : {})}>
        {styles?.hasProfileImage && (
          <LeaderboardAvatar name={name} avatarUrl={avatarUrl} index={index} styles={styles} />
        )}
        <Section className="c-leaderboard-profile__name" textAlign="center">
          {name}
        </Section>
        <VStack w="100%">
          {styles?.hasProgressBar && (
            <ChakraProgress.Root className="c-leaderboard-profile__progress" w="213px" defaultValue={progress}>
              <ChakraProgress.Track bgColor={progressBackgroundColor}>
                <ChakraProgress.Range bgColor={progressColor} />
              </ChakraProgress.Track>
            </ChakraProgress.Root>
          )}
          {styles?.hasAmountRaised && (
            <Body2 className="c-leaderboard-profile__goal">
              <strong>${toDisplayAmountNoDecimal(amountRaised)}</strong> raised of ${toDisplayAmountNoDecimal(goal)}
            </Body2>
          )}
        </VStack>
      </a>
    </VStack>
  )
}

export default LeaderboardProfile
